import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { 
  Building2, Plus, Edit, Trash2, Calendar as CalendarIcon, 
  DollarSign, TrendingUp, MapPin, Eye, Link, Upload, X, Star, Image as ImageIcon,
  Phone, User, Clock, CreditCard, Mail, Landmark, Save, FileText, 
  ArrowUpRight, ArrowDownRight, BarChart3, Filter, Wallet, Info, Percent, KeyRound, EyeOff
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddFacilityForm from '@/components/AddFacilityForm';
import ProfileSettings from '@/components/ProfileSettings';
import { cities, getDistricts, facilityFeatures, sportBranches } from '@/data/turkeyData';
import { FACILITY_TYPES, getFacilityTypeLabel } from '@/data/facilityTypes';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const OperatorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [facilities, setFacilities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);
  
  // Slot management state
  const [selectedFacilityForSlots, setSelectedFacilityForSlots] = useState(null);
  const [slotDate, setSlotDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bulkSlotLoading, setBulkSlotLoading] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState(new Date());
  const [bulkEndDate, setBulkEndDate] = useState(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)); // 30 days ahead
  const [bulkHours, setBulkHours] = useState([9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]);
  const [bulkDays, setBulkDays] = useState([0, 1, 2, 3, 4, 5, 6]); // All days

  // IBAN & Billing state
  const [ibanInfo, setIbanInfo] = useState({
    account_holder: '',
    bank_name: '',
    iban: ''
  });
  const [billingInfo, setBillingInfo] = useState({
    billing_type: 'corporate',
    company_name: '',
    tax_office: '',
    tax_number: '',
    billing_address: '',
    billing_city: '',
    billing_district: ''
  });
  const [ibanLoading, setIbanLoading] = useState(false);
  const [ibanSaving, setIbanSaving] = useState(false);
  const [billingAvailableDistricts, setBillingAvailableDistricts] = useState([]);
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    facility_type: 'hali_saha',
    city: '',
    district: '',
    address: '',
    google_maps_link: '',
    price_per_hour: '',
    features: [],
    branches: [],
    description: '',
    images: [] // [{url, isMain}]
  });

  // Earnings filter state
  const [earningsPeriod, setEarningsPeriod] = useState('all');

  useEffect(() => {
    fetchData();
    fetchIbanAndBilling();
  }, []);

  useEffect(() => {
    if (billingInfo.billing_city) {
      setBillingAvailableDistricts(getDistricts(billingInfo.billing_city));
    } else {
      setBillingAvailableDistricts([]);
    }
  }, [billingInfo.billing_city]);

  useEffect(() => {
    if (formData.city) {
      setAvailableDistricts(getDistricts(formData.city));
      // Reset district if city changed
      if (!getDistricts(formData.city).includes(formData.district)) {
        setFormData(prev => ({ ...prev, district: '' }));
      }
    } else {
      setAvailableDistricts([]);
    }
  }, [formData.city]);

  const fetchData = async () => {
    try {
      const [facilitiesRes, bookingsRes, earningsRes] = await Promise.all([
        axios.get(`${API}/operator/facilities`, { withCredentials: true }),
        axios.get(`${API}/operator/bookings`, { withCredentials: true }),
        axios.get(`${API}/operator/earnings`, { withCredentials: true })
      ]);
      setFacilities(facilitiesRes.data || []);
      setBookings(bookingsRes.data || []);
      setEarnings(earningsRes.data || null);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered bookings for earnings based on period
  const filteredEarningsBookings = useMemo(() => {
    const confirmedBookings = bookings.filter(b => b.payment_status === 'CONFIRMED');
    
    if (earningsPeriod === 'all') return confirmedBookings;
    
    const now = new Date();
    let startDate, endDate;
    
    switch (earningsPeriod) {
      case 'today':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        endDate = new Date();
        break;
      case 'week':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      default:
        return confirmedBookings;
    }
    
    return confirmedBookings.filter(b => {
      try {
        const bookingDate = new Date(b.date);
        return isWithinInterval(bookingDate, { start: startDate, end: endDate });
      } catch {
        return false;
      }
    });
  }, [bookings, earningsPeriod]);

  // Earnings stats
  const earningsStats = useMemo(() => {
    const totalBrut = filteredEarningsBookings.reduce((sum, b) => sum + (b.brut_tutar || 0), 0);
    const totalKomisyon = filteredEarningsBookings.reduce((sum, b) => sum + (b.komisyon_tutar || b.komisyon || 0), 0);
    const totalNet = filteredEarningsBookings.reduce((sum, b) => sum + (b.isletmeci_hakki || 0), 0);
    
    // Facility breakdown
    const facilityBreakdown = facilities.map(f => {
      const facilityBookings = filteredEarningsBookings.filter(b => b.facility_id === f.facility_id);
      const brut = facilityBookings.reduce((sum, b) => sum + (b.brut_tutar || 0), 0);
      const net = facilityBookings.reduce((sum, b) => sum + (b.isletmeci_hakki || 0), 0);
      return {
        ...f,
        bookingCount: facilityBookings.length,
        brut,
        net
      };
    }).sort((a, b) => b.net - a.net);
    
    // Daily earnings for last 7 days
    const dailyEarnings = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), 6 - i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayBookings = bookings.filter(b => 
        b.date === dateStr && b.payment_status === 'CONFIRMED'
      );
      return {
        date: dateStr,
        label: format(date, 'dd MMM', { locale: tr }),
        brut: dayBookings.reduce((sum, b) => sum + (b.brut_tutar || 0), 0),
        net: dayBookings.reduce((sum, b) => sum + (b.isletmeci_hakki || 0), 0),
        count: dayBookings.length
      };
    });
    
    return {
      totalBrut,
      totalKomisyon,
      totalNet,
      bookingCount: filteredEarningsBookings.length,
      avgTicket: filteredEarningsBookings.length > 0 ? totalBrut / filteredEarningsBookings.length : 0,
      effectiveCommissionRate: totalBrut > 0 ? (totalKomisyon / totalBrut) * 100 : 0,
      facilityBreakdown,
      dailyEarnings
    };
  }, [filteredEarningsBookings, facilities, bookings]);

  const fetchIbanAndBilling = async () => {
    setIbanLoading(true);
    try {
      const response = await axios.get(`${API}/user/billing`, {
        withCredentials: true
      });
      if (response.data.iban_info) {
        setIbanInfo(prev => ({
          ...prev,
          ...response.data.iban_info
        }));
      }
      if (response.data.billing_info) {
        setBillingInfo(prev => ({
          ...prev,
          ...response.data.billing_info
        }));
      }
    } catch (error) {
      console.error('Error fetching IBAN/billing info:', error);
    } finally {
      setIbanLoading(false);
    }
  };

  const saveIbanInfo = async () => {
    setIbanSaving(true);
    try {
      await axios.put(`${API}/user/iban`, ibanInfo, {
        withCredentials: true
      });
      toast.success('IBAN bilgileri kaydedildi');
    } catch (error) {
      console.error('Error saving IBAN info:', error);
      toast.error(error.response?.data?.detail || 'IBAN bilgileri kaydedilemedi');
    } finally {
      setIbanSaving(false);
    }
  };

  const saveBillingInfo = async () => {
    setIbanSaving(true);
    try {
      await axios.put(`${API}/user/billing`, billingInfo, {
        withCredentials: true
      });
      toast.success('Fatura bilgileri kaydedildi');
    } catch (error) {
      console.error('Error saving billing info:', error);
      toast.error('Fatura bilgileri kaydedilemedi');
    } finally {
      setIbanSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== newPasswordConfirm) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Yeni şifre en az 8 karakter olmalı');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        withCredentials: true
      });
      toast.success('Şifreniz başarıyla değiştirildi');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setPasswordLoading(false);
    }
  };

  const fetchBookingDetail = async (bookingId) => {
    setBookingDetailLoading(true);
    try {
      const response = await axios.get(`${API}/operator/bookings/${bookingId}`, {
        withCredentials: true
      });
      setSelectedBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking detail:', error);
      toast.error('Rezervasyon detayları yüklenemedi');
    } finally {
      setBookingDetailLoading(false);
    }
  };

  const fetchSlots = async (facilityId, date) => {
    setSlotsLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API}/operator/facilities/${facilityId}/slots?date=${dateStr}`,
        { withCredentials: true }
      );
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Saatler yüklenemedi');
    } finally {
      setSlotsLoading(false);
    }
  };

  const toggleSlot = async (facilityId, hour) => {
    try {
      const dateStr = format(slotDate, 'yyyy-MM-dd');
      const response = await axios.post(
        `${API}/operator/facilities/${facilityId}/slots/toggle`,
        { facility_id: facilityId, date: dateStr, hour },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchSlots(facilityId, slotDate);
    } catch (error) {
      console.error('Error toggling slot:', error);
      toast.error(error.response?.data?.detail || 'Saat değiştirilemedi');
    }
  };

  const handleSelectFacilityForSlots = (facility) => {
    setSelectedFacilityForSlots(facility);
    setSlotDate(new Date());
    fetchSlots(facility.facility_id, new Date());
  };

  const handleSlotDateChange = (date) => {
    if (date && selectedFacilityForSlots) {
      setSlotDate(date);
      fetchSlots(selectedFacilityForSlots.facility_id, date);
    }
  };

  const openAllSlotsForDay = async () => {
    if (!selectedFacilityForSlots) return;
    
    setSlotsLoading(true);
    try {
      const dateStr = format(slotDate, 'yyyy-MM-dd');
      const closedSlots = slots.filter(s => s.status === 'closed').map(s => s.hour);
      
      if (closedSlots.length === 0) {
        toast.info('Tum saatler zaten acik');
        return;
      }
      
      await axios.post(
        `${API}/operator/facilities/${selectedFacilityForSlots.facility_id}/slots/open`,
        { date: dateStr, hours: closedSlots },
        { withCredentials: true }
      );
      toast.success('Tum saatler acildi');
      fetchSlots(selectedFacilityForSlots.facility_id, slotDate);
    } catch (error) {
      toast.error('Saatler acilamadi');
    } finally {
      setSlotsLoading(false);
    }
  };

  const closeAllSlotsForDay = async () => {
    if (!selectedFacilityForSlots) return;
    
    setSlotsLoading(true);
    try {
      const dateStr = format(slotDate, 'yyyy-MM-dd');
      const openSlots = slots.filter(s => s.status === 'open').map(s => s.hour);
      
      if (openSlots.length === 0) {
        toast.info('Tum saatler zaten kapali');
        return;
      }
      
      await axios.post(
        `${API}/operator/facilities/${selectedFacilityForSlots.facility_id}/slots/close`,
        { date: dateStr, hours: openSlots },
        { withCredentials: true }
      );
      toast.success('Tum saatler kapatildi');
      fetchSlots(selectedFacilityForSlots.facility_id, slotDate);
    } catch (error) {
      toast.error('Saatler kapatilamadi');
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleBulkOpen = async () => {
    if (!selectedFacilityForSlots || bulkHours.length === 0) return;
    
    setBulkSlotLoading(true);
    try {
      const response = await axios.post(
        `${API}/operator/facilities/${selectedFacilityForSlots.facility_id}/slots/bulk-open`,
        {
          start_date: format(bulkStartDate, 'yyyy-MM-dd'),
          end_date: format(bulkEndDate, 'yyyy-MM-dd'),
          hours: bulkHours,
          days_of_week: bulkDays
        },
        { withCredentials: true }
      );
      toast.success(`${response.data.count} saat acildi`);
      setShowBulkDialog(false);
      fetchSlots(selectedFacilityForSlots.facility_id, slotDate);
    } catch (error) {
      toast.error('Toplu acma basarisiz');
    } finally {
      setBulkSlotLoading(false);
    }
  };

  const toggleBulkHour = (hour) => {
    setBulkHours(prev => 
      prev.includes(hour) ? prev.filter(h => h !== hour) : [...prev, hour].sort((a,b) => a-b)
    );
  };

  const toggleBulkDay = (day) => {
    setBulkDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a,b) => a-b)
    );
  };

  const resetForm = () => {
    setFormData({
      name: '',
      facility_type: 'hali_saha',
      city: '',
      district: '',
      address: '',
      google_maps_link: '',
      price_per_hour: '',
      features: [],
      branches: [],
      description: '',
      images: []
    });
    setEditingFacility(null);
    setAvailableDistricts([]);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);

    for (const file of files) {
      try {
        // Compress and resize image
        const compressedBase64 = await compressImage(file, 800, 0.7);
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, { 
            url: compressedBase64,
            isMain: prev.images.length === 0
          }]
        }));
      } catch (error) {
        console.error('Image processing error:', error);
        toast.error('Resim işlenemedi');
      }
    }
    
    setUploadingImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Image compression function
  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      // If removed image was main, make first one main
      if (prev.images[index]?.isMain && newImages.length > 0) {
        newImages[0].isMain = true;
      }
      return { ...prev, images: newImages };
    });
  };

  const setMainImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isMain: i === index
      }))
    }));
  };

  const toggleFeature = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const toggleBranch = (branchId) => {
    setFormData(prev => ({
      ...prev,
      branches: prev.branches.includes(branchId)
        ? prev.branches.filter(b => b !== branchId)
        : [...prev.branches, branchId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Sort images - main first
    const sortedImages = [...formData.images].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0));
    
    const payload = {
      name: formData.name,
      facility_type: formData.facility_type,
      city: formData.city,
      district: formData.district,
      address: formData.address,
      google_maps_link: formData.google_maps_link || null,
      price_per_hour: parseFloat(formData.price_per_hour),
      features: formData.features,
      branches: formData.branches,
      description: formData.description || null,
      images: sortedImages.map(img => img.url)
    };

    try {
      if (editingFacility) {
        await axios.put(`${API}/operator/facilities/${editingFacility.facility_id}`, payload, {
          withCredentials: true
        });
        toast.success('Tesis güncellendi');
      } else {
        await axios.post(`${API}/operator/facilities`, payload, {
          withCredentials: true
        });
        toast.success('Tesis eklendi! Admin onayı bekleniyor.');
      }
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (facility) => {
    setEditingFacility(facility);
    setFormData({
      name: facility.name,
      facility_type: facility.facility_type,
      city: facility.city,
      district: facility.district,
      address: facility.address,
      google_maps_link: facility.google_maps_link || '',
      price_per_hour: facility.price_per_hour.toString(),
      features: facility.features || [],
      branches: facility.branches || [],
      description: facility.description || '',
      images: (facility.images || []).map((url, i) => ({ url, isMain: i === 0 }))
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (facilityId) => {
    if (!window.confirm('Bu tesisi silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/operator/facilities/${facilityId}`, {
        withCredentials: true
      });
      toast.success('Tesis silindi');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Silme işlemi başarısız');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800">Onay Bekliyor</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-800">Onaylandı</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Reddedildi</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const facilityTypes = FACILITY_TYPES;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="operator-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-emerald-600" />
              İşletmeci Paneli
            </h1>
            <p className="text-slate-600">{user?.name}</p>
          </div>

          <div className="flex items-center gap-3">
            {user?.auth_type === 'email' && (
              <Button 
                variant="outline"
                onClick={() => setShowPasswordDialog(true)}
                className="border-slate-300"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Şifre Değiştir
              </Button>
            )}
            
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-facility-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Tesis Ekle
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingFacility ? 'Tesis Düzenle' : 'Yeni Tesis Ekle'}
                </DialogTitle>
              </DialogHeader>
              <AddFacilityForm
                initialData={editingFacility}
                onSuccess={() => {
                  setShowAddDialog(false);
                  fetchData();
                }}
                onCancel={() => setShowAddDialog(false)}
                endpoint={editingFacility ? `${API}/operator/facilities/${editingFacility.facility_id}` : `${API}/operator/facilities`}
              />
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Toplam Tesis</p>
                  <p className="text-3xl font-bold text-slate-900">{facilities.length}</p>
                </div>
                <Building2 className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Rezervasyonlar</p>
                  <p className="text-3xl font-bold text-slate-900">{earnings?.total_bookings || 0}</p>
                </div>
                <CalendarIcon className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Brüt Gelir</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {(earnings?.total_brut || 0).toFixed(0)} ₺
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Net Kazanç</p>
                  <p className="text-3xl font-bold text-emerald-700">
                    {(earnings?.total_net || 0).toFixed(0)} ₺
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="facilities">
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="facilities" data-testid="tab-facilities">
              Tesislerim ({facilities.length})
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              <Clock className="w-4 h-4 mr-1" />
              Saat Yönetimi
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              Rezervasyonlar ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="earnings" data-testid="tab-earnings">
              Kazançlar
            </TabsTrigger>
            <TabsTrigger value="payment-info" data-testid="tab-payment-info">
              <Landmark className="w-4 h-4 mr-1" />
              Ödeme Bilgileri
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-1" />
              Profil Ayarları
            </TabsTrigger>
          </TabsList>

          {/* Facilities Tab */}
          <TabsContent value="facilities">
            <Card>
              <CardContent className="p-6">
                {facilities.length > 0 ? (
                  <div className="space-y-4">
                    {facilities.map((facility) => (
                      <div 
                        key={facility.facility_id}
                        className="border border-slate-200 rounded-xl p-4 hover:border-emerald-200 transition-colors"
                        data-testid={`facility-item-${facility.facility_id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex gap-4">
                            {facility.images?.[0] && (
                              <img 
                                src={facility.images[0]} 
                                alt={facility.name}
                                className="w-20 h-20 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-bold text-slate-900">{facility.name}</h3>
                                {getStatusBadge(facility.status)}
                              </div>
                              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {facility.district}, {facility.city}
                                </span>
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-4 h-4" />
                                  {facility.price_per_hour} ₺/saat
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/tesis/${facility.facility_id}`)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(facility)}
                              data-testid={`edit-facility-${facility.facility_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(facility.facility_id)}
                              data-testid={`delete-facility-${facility.facility_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Building2 className="w-16 h-16 text-slate-300 mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      Henüz tesisiniz yok
                    </h3>
                    <p className="text-slate-600 mb-4">
                      İlk tesisinizi ekleyerek başlayın!
                    </p>
                    <Button 
                      onClick={() => setShowAddDialog(true)}
                      className="bg-emerald-700 hover:bg-emerald-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Tesis Ekle
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Management Tab */}
          <TabsContent value="schedule">
            {/* ... schedule content ... */}
            <Card>
              <CardContent className="p-6">
                {/* Facility Selection - Horizontal Pills */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Tesis:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {facilities.filter(f => f.status === 'approved').length > 0 ? (
                      facilities.filter(f => f.status === 'approved').map((facility) => (
                        <button
                          key={facility.facility_id}
                          onClick={() => handleSelectFacilityForSlots(facility)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            selectedFacilityForSlots?.facility_id === facility.facility_id
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {facility.name}
                        </button>
                      ))
                    ) : (
                      <p className="text-slate-500 text-sm">Onaylanmis tesisiniz bulunmuyor</p>
                    )}
                  </div>
                </div>

                {selectedFacilityForSlots ? (
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left: Calendar + Quick Actions */}
                    <div className="lg:w-80 space-y-4">
                      <Calendar
                        mode="single"
                        selected={slotDate}
                        onSelect={handleSlotDateChange}
                        locale={tr}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        className="rounded-lg border mx-auto"
                      />
                      
                      {/* Quick Actions */}
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={openAllSlotsForDay}
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                        >
                          Tum Gunu Ac
                        </Button>
                        <Button
                          variant="outline"
                          onClick={closeAllSlotsForDay}
                          className="w-full"
                        >
                          Tum Gunu Kapat
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowBulkDialog(true)}
                          className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          Toplu Ac (Tarih Araligi)
                        </Button>
                      </div>
                    </div>

                    {/* Right: Slots Grid */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-lg text-slate-800">
                          {format(slotDate, 'd MMMM yyyy, EEEE', { locale: tr })}
                        </h3>
                        <div className="flex gap-3 text-xs">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-emerald-500 rounded"></div>
                            <span>Acik</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-slate-300 rounded"></div>
                            <span>Kapali</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-blue-500 rounded"></div>
                            <span>Rezerveli</span>
                          </div>
                        </div>
                      </div>

                      {slotsLoading ? (
                        <div className="flex justify-center py-12">
                          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                            {slots.map((slot) => (
                              <button
                                key={slot.hour}
                                onClick={() => {
                                  if (slot.status !== 'booked') {
                                    toggleSlot(selectedFacilityForSlots.facility_id, slot.hour);
                                  }
                                }}
                                disabled={slot.status === 'booked'}
                                className={`p-3 rounded-lg text-center font-medium transition-all ${
                                  slot.status === 'open'
                                    ? 'bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer shadow-sm'
                                    : slot.status === 'closed'
                                    ? 'bg-slate-100 text-slate-500 hover:bg-slate-200 cursor-pointer'
                                    : 'bg-blue-500 text-white cursor-not-allowed shadow-sm'
                                }`}
                                title={
                                  slot.status === 'booked' 
                                    ? `Rezerveli: ${slot.booking_info?.booking_code}` 
                                    : slot.status === 'closed' 
                                    ? 'Kapali - Tiklayarak acin' 
                                    : 'Acik - Tiklayarak kapatin'
                                }
                              >
                                <span className="text-sm">{slot.time}</span>
                                {slot.status === 'booked' && (
                                  <span className="block text-xs mt-0.5">
                                    {slot.booking_info?.status === 'CONFIRMED' ? '✓' : '⏳'}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                          
                          <div className="mt-4 p-3 bg-slate-50 rounded-lg text-center">
                            <p className="text-xs text-slate-600">
                              Saatlere tiklayarak acip kapatabilirsiniz. Varsayilan: Kapali
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg">Saat yonetimi icin yukaridan bir tesis secin</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardContent className="p-6">
                {bookings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Rezervasyon Kodu</th>
                          <th>Tesis</th>
                          <th>Tarih</th>
                          <th>Saat</th>
                          <th>Tutar</th>
                          <th>Durum</th>
                          <th>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((booking) => (
                          <tr key={booking.booking_id} data-testid={`booking-row-${booking.booking_id}`}>
                            <td className="font-mono text-sm text-emerald-600">{booking.booking_code}</td>
                            <td className="font-medium">{booking.facility_name}</td>
                            <td>{format(new Date(booking.date), 'd MMM yyyy', { locale: tr })}</td>
                            <td>{booking.start_time} - {booking.end_time}</td>
                            <td className="font-bold">{booking.brut_tutar.toFixed(2)} ₺</td>
                            <td>
                              {booking.payment_status === 'CONFIRMED' ? (
                                <Badge className="status-confirmed">Onaylandı</Badge>
                              ) : booking.payment_status === 'PENDING_PAYMENT' ? (
                                <Badge className="status-pending">Bekliyor</Badge>
                              ) : (
                                <Badge className="status-cancelled">İptal</Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fetchBookingDetail(booking.booking_id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Detay
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <CalendarIcon className="w-16 h-16 text-slate-300 mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      Henüz rezervasyon yok
                    </h3>
                    <p className="text-slate-600">
                      Tesisiniz onaylandığında rezervasyonlar burada görünecek.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            {/* Period Filter */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-slate-400" />
                    <span className="font-medium text-slate-700">Dönem:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'Tüm Zamanlar' },
                      { value: 'today', label: 'Bugün' },
                      { value: 'week', label: 'Bu Hafta' },
                      { value: 'month', label: 'Bu Ay' }
                    ].map(period => (
                      <Button
                        key={period.value}
                        size="sm"
                        variant={earningsPeriod === period.value ? 'default' : 'outline'}
                        onClick={() => setEarningsPeriod(period.value)}
                        className={earningsPeriod === period.value ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                      >
                        {period.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-300 text-sm">Brüt Gelir</p>
                      <p className="text-2xl font-bold">{earningsStats.totalBrut.toFixed(0)} ₺</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-slate-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Platform Komisyonu</p>
                      <p className="text-2xl font-bold">-{earningsStats.totalKomisyon.toFixed(0)} ₺</p>
                      <p className="text-amber-200 text-xs">Ort: %{earningsStats.effectiveCommissionRate.toFixed(1)}</p>
                    </div>
                    <CreditCard className="w-8 h-8 text-amber-300" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm">Net Kazanç</p>
                      <p className="text-2xl font-bold">{earningsStats.totalNet.toFixed(0)} ₺</p>
                    </div>
                    <Wallet className="w-8 h-8 text-emerald-300" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Rezervasyon</p>
                      <p className="text-2xl font-bold">{earningsStats.bookingCount}</p>
                      <p className="text-blue-200 text-xs">Ort: {earningsStats.avgTicket.toFixed(0)} ₺</p>
                    </div>
                    <CalendarIcon className="w-8 h-8 text-blue-300" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Earnings Chart */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  Son 7 Gün Kazanç Grafiği
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48 flex items-end justify-between gap-2">
                  {earningsStats.dailyEarnings.map((day) => {
                    const maxNet = Math.max(...earningsStats.dailyEarnings.map(d => d.net), 1);
                    const heightPercent = (day.net / maxNet) * 100;
                    
                    return (
                      <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full flex flex-col items-center">
                          <span className="text-xs font-bold text-emerald-600 mb-1">
                            {day.net > 0 ? `${day.net.toFixed(0)}₺` : '-'}
                          </span>
                          <div 
                            className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-lg transition-all duration-300 hover:from-emerald-600 hover:to-emerald-500"
                            style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: '8px' }}
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-medium text-slate-700">{day.label}</p>
                          <p className="text-xs text-slate-400">{day.count} rez.</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Facility Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Tesis Bazlı Kazançlar
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-xs font-medium text-slate-600">Tesis</th>
                          <th className="text-center p-3 text-xs font-medium text-slate-600">Rez.</th>
                          <th className="text-right p-3 text-xs font-medium text-slate-600">Brüt</th>
                          <th className="text-right p-3 text-xs font-medium text-slate-600">Net</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {earningsStats.facilityBreakdown.map((f) => (
                          <tr key={f.facility_id} className="hover:bg-slate-50">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {f.images?.[0] ? (
                                  <img src={f.images[0]} alt="" className="w-8 h-8 rounded object-cover" />
                                ) : (
                                  <div className="w-8 h-8 bg-slate-200 rounded flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-slate-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-sm text-slate-900 truncate max-w-[150px]">{f.name}</p>
                                  <p className="text-xs text-slate-500">{f.city}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge className="bg-slate-100 text-slate-700">{f.bookingCount}</Badge>
                            </td>
                            <td className="p-3 text-right">
                              <span className="text-slate-600">{f.brut.toFixed(0)} ₺</span>
                            </td>
                            <td className="p-3 text-right">
                              <span className="font-bold text-emerald-600">{f.net.toFixed(0)} ₺</span>
                            </td>
                          </tr>
                        ))}
                        {earningsStats.facilityBreakdown.length === 0 && (
                          <tr>
                            <td colSpan={4} className="p-4 text-center text-slate-500">
                              Henüz tesis yok
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {earningsStats.facilityBreakdown.length > 0 && (
                        <tfoot className="bg-emerald-50">
                          <tr>
                            <td className="p-3 font-bold text-emerald-800">TOPLAM</td>
                            <td className="p-3 text-center font-bold text-emerald-800">
                              {earningsStats.bookingCount}
                            </td>
                            <td className="p-3 text-right font-bold text-emerald-800">
                              {earningsStats.totalBrut.toFixed(0)} ₺
                            </td>
                            <td className="p-3 text-right font-bold text-emerald-800">
                              {earningsStats.totalNet.toFixed(0)} ₺
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Commission Info Card */}
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <Percent className="w-5 h-5" />
                    Komisyon Oranları Bilgilendirmesi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-purple-700 mb-4">
                    Platform komisyon oranları, tesisinizin haftalık rezervasyon sayısına göre dinamik olarak belirlenir. 
                    Daha fazla rezervasyon = daha düşük komisyon!
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <span className="text-red-600 font-bold text-sm">1-3</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Başlangıç Seviyesi</p>
                          <p className="text-xs text-slate-500">Haftalık 1-3 rezervasyon</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-600">%20</p>
                        <p className="text-xs text-slate-500">Komisyon</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-amber-600 font-bold text-sm">4-6</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Orta Seviye</p>
                          <p className="text-xs text-slate-500">Haftalık 4-6 rezervasyon</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-amber-600">%15</p>
                        <p className="text-xs text-slate-500">Komisyon</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-600 font-bold text-sm">7+</span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Premium Seviye</p>
                          <p className="text-xs text-slate-500">Haftalık 7+ rezervasyon</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600">%10</p>
                        <p className="text-xs text-slate-500">Komisyon</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                    <p className="text-xs text-purple-700 flex items-start gap-2">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      Komisyon oranı, her tesis için ayrı ayrı ve haftalık bazda hesaplanır. Yeni haftaya geçildiğinde sayaç sıfırlanır.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Earnings */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Son Kazançlar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {filteredEarningsBookings.slice(0, 10).map((booking) => (
                    <div key={booking.booking_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm text-slate-900">{booking.facility_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                          <span>{format(new Date(booking.date), 'd MMM', { locale: tr })}</span>
                          <span>•</span>
                          <span>{booking.start_time} - {booking.end_time}</span>
                          {(booking.komisyon_orani ?? booking.commission_rate) && (
                            <>
                              <span>•</span>
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                %{((booking.komisyon_orani ?? booking.commission_rate) * 100).toFixed(0)} komisyon
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">+{(booking.isletmeci_hakki || 0).toFixed(0)} ₺</p>
                        <p className="text-xs text-slate-400">Brüt: {(booking.brut_tutar || 0).toFixed(0)} ₺</p>
                      </div>
                    </div>
                  ))}
                  {filteredEarningsBookings.length === 0 && (
                    <div className="text-center py-8">
                      <Wallet className="w-12 h-12 text-slate-200 mx-auto mb-2" />
                      <p className="text-slate-500">Bu dönemde kazanç yok</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment Info Card */}
            <Card className="mt-6 bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Landmark className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900">Ödeme Bilgilendirmesi</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Kazançlarınız haftalık olarak IBAN hesabınıza aktarılır. 
                      Ödeme altyapısı (PayTR) yakında aktif olacaktır. Şu anda tüm ödemeler mock olarak işlenmektedir.
                    </p>
                    {!ibanInfo.iban && (
                      <div className="mt-3 p-2 bg-amber-100 rounded-lg">
                        <p className="text-sm text-amber-800">
                          ⚠️ IBAN bilgilerinizi &quot;Ödeme Bilgileri&quot; sekmesinden girmeyi unutmayın!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Info Tab - IBAN & Billing */}
          <TabsContent value="payment-info">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* IBAN Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-emerald-600" />
                    IBAN Bilgileri
                  </CardTitle>
                  <p className="text-sm text-slate-500">Kazançlarınızın aktarılacağı banka hesabı</p>
                </CardHeader>
                <CardContent>
                  {ibanLoading ? (
                    <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Hesap Sahibi Ad Soyad *</Label>
                        <Input
                          value={ibanInfo.account_holder || ''}
                          onChange={(e) => setIbanInfo({...ibanInfo, account_holder: e.target.value})}
                          placeholder="Ad Soyad"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Banka Adı *</Label>
                        <Select 
                          value={ibanInfo.bank_name || ''} 
                          onValueChange={(v) => setIbanInfo({...ibanInfo, bank_name: v})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Banka seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {['Ziraat Bankası', 'İş Bankası', 'Garanti BBVA', 'Yapı Kredi', 'Akbank', 
                              'QNB Finansbank', 'Denizbank', 'Halkbank', 'Vakıfbank', 'TEB', 'ING Bank',
                              'HSBC', 'Şekerbank', 'Odeabank', 'Alternatifbank', 'Diğer'].map(bank => (
                              <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>IBAN Numarası *</Label>
                        <Input
                          value={ibanInfo.iban || ''}
                          onChange={(e) => setIbanInfo({...ibanInfo, iban: e.target.value.toUpperCase()})}
                          placeholder="TR00 0000 0000 0000 0000 0000 00"
                          className="mt-1 font-mono"
                          maxLength={32}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          TR ile başlayan 26 haneli IBAN numaranız
                        </p>
                      </div>

                      <div className="flex justify-end pt-4 border-t">
                        <Button 
                          onClick={saveIbanInfo}
                          disabled={ibanSaving || !ibanInfo.account_holder || !ibanInfo.bank_name || !ibanInfo.iban}
                          className="bg-emerald-700 hover:bg-emerald-800"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {ibanSaving ? 'Kaydediliyor...' : 'IBAN Bilgilerini Kaydet'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fatura Bilgileri */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Fatura Bilgileri
                  </CardTitle>
                  <p className="text-sm text-slate-500">Kesilen faturalar için firma bilgileriniz</p>
                </CardHeader>
                <CardContent>
                  {ibanLoading ? (
                    <div className="h-40 bg-slate-100 rounded-lg animate-pulse" />
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Fatura Tipi</Label>
                        <Select 
                          value={billingInfo.billing_type} 
                          onValueChange={(v) => setBillingInfo({...billingInfo, billing_type: v})}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="individual">Bireysel</SelectItem>
                            <SelectItem value="corporate">Kurumsal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {billingInfo.billing_type === 'corporate' ? (
                        <>
                          <div>
                            <Label>Şirket/Firma Adı</Label>
                            <Input
                              value={billingInfo.company_name || ''}
                              onChange={(e) => setBillingInfo({...billingInfo, company_name: e.target.value})}
                              placeholder="Şirket adı"
                              className="mt-1"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Vergi Dairesi</Label>
                              <Input
                                value={billingInfo.tax_office || ''}
                                onChange={(e) => setBillingInfo({...billingInfo, tax_office: e.target.value})}
                                placeholder="Vergi dairesi"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Vergi Numarası</Label>
                              <Input
                                value={billingInfo.tax_number || ''}
                                onChange={(e) => setBillingInfo({...billingInfo, tax_number: e.target.value})}
                                placeholder="Vergi numarası"
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div>
                          <Label>TC Kimlik Numarası</Label>
                          <Input
                            value={billingInfo.tax_number || ''}
                            onChange={(e) => setBillingInfo({...billingInfo, tax_number: e.target.value})}
                            placeholder="TC Kimlik numarası"
                            maxLength={11}
                            className="mt-1"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Şehir</Label>
                          <Select 
                            value={billingInfo.billing_city || ''} 
                            onValueChange={(v) => setBillingInfo({...billingInfo, billing_city: v, billing_district: ''})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Şehir seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {cities.map(city => (
                                <SelectItem key={city} value={city}>{city}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>İlçe</Label>
                          <Select 
                            value={billingInfo.billing_district || ''} 
                            onValueChange={(v) => setBillingInfo({...billingInfo, billing_district: v})}
                            disabled={!billingInfo.billing_city}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="İlçe seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {billingAvailableDistricts.map(district => (
                                <SelectItem key={district} value={district}>{district}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Fatura Adresi</Label>
                        <Textarea
                          value={billingInfo.billing_address || ''}
                          onChange={(e) => setBillingInfo({...billingInfo, billing_address: e.target.value})}
                          placeholder="Açık adres"
                          rows={2}
                          className="mt-1"
                        />
                      </div>

                      <div className="flex justify-end pt-4 border-t">
                        <Button 
                          onClick={saveBillingInfo}
                          disabled={ibanSaving}
                          className="bg-emerald-700 hover:bg-emerald-800"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {ibanSaving ? 'Kaydediliyor...' : 'Fatura Bilgilerini Kaydet'}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Bilgilendirme */}
            <Card className="mt-6">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">Ödeme Hakkında</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Kazançlarınız haftalık olarak belirttiğiniz IBAN numarasına aktarılır. 
                      Fatura bilgilerinizi eksiksiz doldurmanız, muhasebe işlemlerinizin sorunsuz yürümesi için önemlidir.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Settings Tab */}
          <TabsContent value="profile">
            <ProfileSettings 
              user={user} 
              onUpdate={() => window.location.reload()}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-emerald-600" />
              Rezervasyon Detayları
            </DialogTitle>
          </DialogHeader>
          
          {bookingDetailLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : selectedBooking && (
            <div className="space-y-6">
              {/* Booking Code */}
              <div className="bg-emerald-50 p-4 rounded-xl text-center">
                <p className="text-sm text-emerald-700 mb-1">Rezervasyon Kodu</p>
                <p className="text-2xl font-bold font-mono text-emerald-800">{selectedBooking.booking_code}</p>
              </div>

              {/* Customer Info */}
              <div className="bg-slate-50 p-4 rounded-xl space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Müşteri Bilgileri
                </h4>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Ad Soyad:</span>
                    <span className="font-medium">{selectedBooking.user_info?.name || 'Belirtilmemiş'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">E-posta:</span>
                    <span className="font-medium">{selectedBooking.user_info?.email || 'Belirtilmemiş'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Telefon:</span>
                    <span className="font-medium">{selectedBooking.user_info?.phone || 'Belirtilmemiş'}</span>
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Rezervasyon Bilgileri
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Tesis</p>
                    <p className="font-medium">{selectedBooking.facility_name}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Tarih</p>
                    <p className="font-medium">
                      {format(new Date(selectedBooking.date), 'd MMMM yyyy, EEEE', { locale: tr })}
                    </p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Saat</p>
                    <p className="font-medium">{selectedBooking.start_time} - {selectedBooking.end_time}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-slate-500 text-xs mb-1">Oluşturulma</p>
                    <p className="font-medium">
                      {format(new Date(selectedBooking.created_at), 'd MMM yyyy HH:mm', { locale: tr })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Ödeme Bilgileri
                </h4>
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Brüt Tutar</span>
                    <span className="font-medium">{selectedBooking.brut_tutar?.toFixed(2)} ₺</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Platform Komisyonu (%{((selectedBooking.komisyon_orani ?? selectedBooking.commission_rate ?? 0.10) * 100).toFixed(0)})</span>
                    <span className="font-medium text-amber-600">-{selectedBooking.komisyon_tutar?.toFixed(2)} ₺</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold">Size Ödenecek</span>
                    <span className="font-bold text-emerald-600">{selectedBooking.isletmeci_hakki?.toFixed(2)} ₺</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-sm text-slate-600">Ödeme Durumu:</span>
                  {selectedBooking.payment_status === 'CONFIRMED' ? (
                    <Badge className="status-confirmed">Onaylandı</Badge>
                  ) : selectedBooking.payment_status === 'PENDING_PAYMENT' ? (
                    <Badge className="status-pending">Ödeme Bekliyor</Badge>
                  ) : (
                    <Badge className="status-cancelled">İptal Edildi</Badge>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBooking(null)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Open Slots Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Toplu Saat Acma</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Baslangic Tarihi</Label>
                <input
                  type="date"
                  value={format(bulkStartDate, 'yyyy-MM-dd')}
                  onChange={(e) => setBulkStartDate(new Date(e.target.value))}
                  className="w-full border rounded-md p-2 mt-1"
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div>
                <Label>Bitis Tarihi</Label>
                <input
                  type="date"
                  value={format(bulkEndDate, 'yyyy-MM-dd')}
                  onChange={(e) => setBulkEndDate(new Date(e.target.value))}
                  className="w-full border rounded-md p-2 mt-1"
                  min={format(bulkStartDate, 'yyyy-MM-dd')}
                />
              </div>
            </div>

            {/* Days of Week */}
            <div>
              <Label className="mb-2 block">Gunler</Label>
              <div className="flex flex-wrap gap-2">
                {['Pzt', 'Sal', 'Car', 'Per', 'Cum', 'Cmt', 'Paz'].map((day, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggleBulkDay(idx)}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      bulkDays.includes(idx)
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            {/* Hours */}
            <div>
              <Label className="mb-2 block">Saatler</Label>
              <div className="grid grid-cols-8 gap-1">
                {Array.from({length: 16}, (_, i) => i + 8).map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => toggleBulkHour(hour)}
                    className={`p-1 rounded text-xs transition-all ${
                      bulkHours.includes(hour)
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {hour}:00
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setBulkHours([9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22])}
                  className="text-xs text-emerald-600 hover:underline"
                >
                  Tum Gun
                </button>
                <button
                  type="button"
                  onClick={() => setBulkHours([18, 19, 20, 21, 22])}
                  className="text-xs text-emerald-600 hover:underline"
                >
                  Aksam
                </button>
                <button
                  type="button"
                  onClick={() => setBulkHours([])}
                  className="text-xs text-slate-500 hover:underline"
                >
                  Temizle
                </button>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Secili:</strong> {bulkDays.length} gun, {bulkHours.length} saat
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
              Iptal
            </Button>
            <Button 
              onClick={handleBulkOpen}
              disabled={bulkSlotLoading || bulkHours.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {bulkSlotLoading ? 'Aciliyor...' : 'Saatleri Ac'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperatorDashboard;
